import React from 'react';
import { CalendarBlank, Clock, MapPin } from '@phosphor-icons/react';

/**
 * Mini live preview of how the public RSVP page will look with the selected theme.
 * Updates instantly as form values change in the EventFormModal.
 */
export default function ThemePreview({ theme, event }) {
  if (!theme) return null;

  const primary = theme.primary || '#FF6B35';
  const accent = theme.accent || primary;
  const bg = theme.background || '#0A0A0A';
  const font = theme.font_family || "'Inter', system-ui, sans-serif";
  const emojis = theme.emojis || ['🎉'];
  const decorations = theme.decorations || [];

  const isLightBg = typeof bg === 'string' && bg.includes('linear-gradient') &&
    !/(#0[0-9a-fA-F]{1})|#1[0-2]/i.test(bg);
  const headingColor = isLightBg ? '#1F1147' : '#FFFFFF';
  const subText = isLightBg ? 'rgba(31,17,71,0.7)' : 'rgba(255,255,255,0.75)';
  const cardBg = isLightBg ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.06)';
  const cardBorder = isLightBg ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.1)';

  const title = event?.title?.trim() || 'Your Event Title';
  const date = event?.event_date ? formatDate(event.event_date) : 'Saturday, 23 May 2026';
  const time = event?.event_time || '4:00 PM';
  const endTime = event?.end_time;
  const venue = event?.venue || 'Your venue address';
  const cover = event?.cover_message;
  const eventEmoji = event?.theme_emoji;

  return (
    <div
      data-testid="theme-live-preview"
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: bg,
        fontFamily: font,
        height: 220,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: `0 12px 30px ${primary}20`,
      }}
    >
      {/* Theme tag pill (top-right) */}
      <div
        className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
        style={{
          background: `linear-gradient(135deg, ${primary}, ${accent})`,
          color: '#FFFFFF',
          letterSpacing: 1,
        }}
      >
        {theme.name}
      </div>

      {/* Decorative emojis (deterministic positions) */}
      {[
        { top: 8, left: 10, size: 16, delay: '0s' },
        { top: 18, right: 60, size: 13, delay: '0.4s' },
        { top: 78, left: 14, size: 14, delay: '0.9s' },
        { top: 92, right: 14, size: 17, delay: '0.2s' },
        { bottom: 70, left: 20, size: 14, delay: '0.6s' },
        { bottom: 14, right: 24, size: 15, delay: '1.1s' },
      ].map((p, i) => (
        <div
          key={i}
          className="floating-mini-emoji absolute select-none"
          style={{
            top: p.top, bottom: p.bottom, left: p.left, right: p.right,
            fontSize: p.size, opacity: 0.5, animationDelay: p.delay,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))',
            pointerEvents: 'none',
          }}
        >
          {emojis[i % emojis.length]}
        </div>
      ))}

      {/* Wave at bottom for mermaid/water themes */}
      {decorations.includes('waves') && (
        <svg viewBox="0 0 360 80" preserveAspectRatio="none"
          className="absolute bottom-0 left-0 w-full" style={{ height: 36, opacity: 0.5, pointerEvents: 'none' }}>
          <path d="M0,40 C90,80 180,0 270,40 C320,60 360,40 360,40 L360,80 L0,80 Z" fill={primary} opacity="0.45" />
          <path d="M0,55 C90,30 180,75 270,55 C320,40 360,55 360,55 L360,80 L0,80 Z" fill={primary} opacity="0.7" />
        </svg>
      )}

      {/* Polka pattern overlay */}
      {decorations.includes('polka') && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.65) 1.5px, transparent 1.5px)',
            backgroundSize: '14px 14px',
            opacity: 0.18,
          }}
        />
      )}

      {/* Content card */}
      <div className="absolute inset-0 flex items-center justify-center p-3 z-10">
        <div
          className="text-center px-4 py-3 rounded-xl backdrop-blur-md max-w-[88%]"
          style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          }}
        >
          {eventEmoji && <div style={{ fontSize: 22, marginBottom: 4, lineHeight: 1 }}>{eventEmoji}</div>}
          <h4
            className="font-bold leading-tight"
            style={{
              color: isLightBg ? primary : '#FFFFFF',
              fontSize: 16,
              fontFamily: font,
              letterSpacing: -0.3,
              margin: '2px 0 4px',
            }}
          >
            {title}
          </h4>
          {cover && (
            <p style={{ color: subText, fontSize: 10, fontStyle: 'italic', margin: '0 0 6px', lineHeight: 1.3 }}>
              "{cover.length > 50 ? cover.slice(0, 50) + '…' : cover}"
            </p>
          )}
          <div
            style={{
              display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
              gap: 6, color: headingColor, fontSize: 9, fontWeight: 600,
              marginTop: 4,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <CalendarBlank size={10} weight="fill" color={primary} /> {date}
            </span>
            {time && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <Clock size={10} weight="fill" color={primary} /> {time}{endTime ? ` – ${endTime}` : ''}
              </span>
            )}
            {venue && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: 220 }}>
                <MapPin size={10} weight="fill" color={primary} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{venue}</span>
              </span>
            )}
          </div>
          <div
            style={{
              display: 'inline-block',
              marginTop: 7,
              padding: '4px 12px',
              borderRadius: 999,
              background: `linear-gradient(135deg, ${primary}, ${accent})`,
              color: '#FFFFFF',
              fontSize: 8.5,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
            }}
          >
            Submit RSVP
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float-mini {
          0%, 100% { transform: translateY(0) rotate(-4deg); }
          50% { transform: translateY(-6px) rotate(4deg); }
        }
        .floating-mini-emoji { animation: float-mini 3.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return dateStr; }
}
